import { useState, useEffect, useRef } from 'react';
import { Socket } from 'socket.io-client';
import toast from 'react-hot-toast';

interface ConsiglioPanelProps {
  sessionId: string;
  socket: Socket | null;
  isConnected: boolean;
}

interface ConsiglioEntry {
  id: string;
  text: string;
  nickname: string;
  team: string;
  timestamp: number;
}

type PanelState = 'idle' | 'active';

const TEAM_BADGE_COLORS: Record<string, string> = {
  BLUE: 'bg-blue-600',
  RED: 'bg-red-600',
  GREEN: 'bg-green-600',
  YELLOW: 'bg-yellow-600',
  blue: 'bg-blue-600',
  red: 'bg-red-600',
  green: 'bg-green-600',
  yellow: 'bg-yellow-600',
};

export function ConsiglioPanel({ sessionId, socket, isConnected }: ConsiglioPanelProps) {
  const [panelState, setPanelState] = useState<PanelState>('idle');
  const [entries, setEntries] = useState<ConsiglioEntry[]>([]);
  const [remainingSeconds, setRemainingSeconds] = useState<number | null>(null);
  const [selectedEntryId, setSelectedEntryId] = useState<string | null>(null);
  const [durationSeconds, setDurationSeconds] = useState(55);
  const listRef = useRef<HTMLDivElement>(null);

  // Socket listeners
  useEffect(() => {
    if (!socket) return;

    const handleNewEntry = (data: { entry: ConsiglioEntry }) => {
      setEntries((prev) => [...prev, data.entry]);
    };

    const handleClosed = () => {
      setPanelState('idle');
      setRemainingSeconds(null);
      // Entries NON vengono cancellate - restano visibili fino alla prossima apertura
    };

    const handleTick = (data: { sessionId: string; remainingSeconds: number }) => {
      setRemainingSeconds(data.remainingSeconds);
    };

    socket.on('consiglio:new-entry', handleNewEntry);
    socket.on('consiglio:closed', handleClosed);
    socket.on('consiglio:tick', handleTick);

    return () => {
      socket.off('consiglio:new-entry', handleNewEntry);
      socket.off('consiglio:closed', handleClosed);
      socket.off('consiglio:tick', handleTick);
    };
  }, [socket]);

  // Auto-scroll on new entries
  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [entries]);

  const handleOpen = async () => {
    if (!isConnected) {
      toast.error('Socket non connesso');
      return;
    }

    try {
      const response = await fetch('/api/consiglio/open', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId, durationSeconds }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Errore apertura consiglio');
      }

      const data = await response.json();
      setPanelState('active');
      setEntries([]); // Nuova sessione: cancella entries precedenti
      setSelectedEntryId(null);
      setRemainingSeconds(data.state?.durationSeconds || durationSeconds);
      toast.success('Sessione consigli aperta!');
    } catch (error: any) {
      console.error('[ConsiglioPanel] Error opening:', error);
      toast.error(error.message || 'Errore apertura consiglio');
    }
  };

  const handleClose = async () => {
    try {
      const response = await fetch('/api/consiglio/close', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Errore chiusura consiglio');
      }

      setPanelState('idle');
      setRemainingSeconds(null);
      // Entries NON vengono cancellate
      toast.success('Sessione consigli chiusa');
    } catch (error: any) {
      console.error('[ConsiglioPanel] Error closing:', error);
      toast.error(error.message || 'Errore chiusura consiglio');
    }
  };

  const handleSelectEntry = async (entryId: string) => {
    try {
      const response = await fetch('/api/consiglio/select-entry', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId, entryId }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Errore selezione entry');
      }

      setSelectedEntryId(entryId);
      toast.success('Consiglio proiettato!');
    } catch (error: any) {
      toast.error(error.message || 'Errore selezione entry');
    }
  };

  const renderEntryList = () => (
    <div
      ref={listRef}
      className="max-h-96 overflow-y-auto space-y-2 pr-1"
    >
      {entries.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          {panelState === 'active' ? 'In attesa di consigli dal pubblico...' : 'Nessun consiglio ricevuto'}
        </div>
      )}

      {entries.map((entry) => (
        <div
          key={entry.id}
          onClick={() => handleSelectEntry(entry.id)}
          className={`rounded-lg p-3 border cursor-pointer transition-colors ${
            selectedEntryId === entry.id
              ? 'bg-teal-900/50 border-teal-500'
              : 'bg-gray-700/50 border-gray-600 hover:bg-gray-600/50 hover:border-gray-500'
          }`}
        >
          <div className="flex items-center gap-2 mb-1">
            <span className="text-white font-semibold text-sm">{entry.nickname}</span>
            <span className={`text-xs px-2 py-0.5 rounded-full text-white ${
              TEAM_BADGE_COLORS[entry.team] || 'bg-gray-600'
            }`}>
              {entry.team}
            </span>
          </div>
          <p className="text-gray-300 text-sm">{entry.text}</p>
        </div>
      ))}
    </div>
  );

  return (
    <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
      <h3 className="text-xl font-bold text-white mb-4">Consiglio</h3>

      {panelState === 'idle' && (
        <>
          <p className="text-gray-400 text-sm mb-4">
            Apri una sessione per raccogliere consigli dal pubblico. Ogni spettatore potra' inviare un messaggio libero (max 200 caratteri).
          </p>

          <div className="flex items-center gap-3 mb-4">
            <label className="text-gray-400 text-sm whitespace-nowrap">Durata:</label>
            <input
              type="text"
              inputMode="numeric"
              value={durationSeconds}
              onChange={(e) => setDurationSeconds(e.target.value as any)}
              onBlur={(e) => { const n = parseInt(e.target.value); setDurationSeconds(isNaN(n) ? 55 : Math.max(10, Math.min(300, n))); }}
              className="w-20 bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm"
            />
            <span className="text-gray-500 text-sm">secondi</span>
          </div>

          <button
            onClick={handleOpen}
            disabled={!isConnected}
            className="w-full bg-teal-600 hover:bg-teal-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white py-3 px-4 rounded-lg font-bold transition-all"
          >
            {entries.length > 0 ? 'Nuova Sessione Consigli' : 'Apri Sessione Consigli'}
          </button>

          {!isConnected && (
            <div className="mt-4 p-3 bg-yellow-900/30 border border-yellow-600 rounded-lg">
              <p className="text-yellow-300 text-sm">
                Socket non connesso. La sessione non puo' essere aperta.
              </p>
            </div>
          )}

          {/* Mostra entries precedenti anche dopo chiusura */}
          {entries.length > 0 && (
            <div className="mt-4">
              <p className="text-gray-400 text-xs mb-2">
                Clicca un consiglio per proiettarlo sul display
              </p>
              {renderEntryList()}
            </div>
          )}
        </>
      )}

      {panelState === 'active' && (
        <>
          {/* Header con counter e countdown */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <span className="bg-teal-600 text-white text-sm px-3 py-1 rounded-full font-bold animate-pulse">
                ATTIVA
              </span>
              {remainingSeconds !== null && (
                <span className={`text-lg font-mono font-bold ${remainingSeconds <= 5 ? 'text-red-400 animate-pulse' : 'text-teal-400'}`}>
                  {remainingSeconds}s
                </span>
              )}
              <span className="text-gray-400 text-sm">
                {entries.length} consiglio{entries.length !== 1 ? 'i' : ''} ricevuto{entries.length !== 1 ? 'i' : ''}
              </span>
            </div>
            <button
              onClick={handleClose}
              className="bg-red-600 hover:bg-red-700 text-white py-2 px-4 rounded-lg font-semibold transition-all text-sm"
            >
              Chiudi
            </button>
          </div>

          <p className="text-gray-400 text-xs mb-2">
            Clicca un consiglio per proiettarlo sul display
          </p>

          {/* Entries list */}
          {renderEntryList()}
        </>
      )}
    </div>
  );
}
